import { apiFetchFull, api } from './client'

export interface OrderLine {
  id: number
  sort_order: number
  name: string
  sku?: string
  stock_sku?: string | null
  /** Sternnummer (stock_items.merchant_sku), nur in der Einzelansicht. */
  merchant_sku?: string | null
  // Backend liefert numeric-Spalten als String ("2.000", "52.8000").
  quantity: string | number
  price: string | number
  tax: string | number
  transaction_id?: string | null
  external_id?: string | null
  /** stock_items.sku der Lager-Variante (z. B. DR-9048), Schluessel fuer Produktfotos. */
  lager_sku?: string | null
  /** Sternnummer und Menge laut Stueckliste (Soll aus Stammdaten, keine Lagerbewegung). */
  stueckliste?: OrderStueckliste
}

export type StuecklistenGrund = 'lagerartikel_fehlt' | 'kein_kopf' | 'keine_sternnummer' | 'komponente_fehlt'
  | 'faktor_ungueltig' | 'verschachtelt' | 'menge_ungueltig'

export interface OrderStueckliste {
  status: 'ok' | 'kein_lagerartikel' | 'unvollstaendig'
  teile: Array<{ kopf_id: number | null; merchant_sku: string | null; name: string | null; menge: number; grund?: StuecklistenGrund }>
}

export interface OrderAddress {
  id?: number
  type: 'billing' | 'shipping'
  salutation?: string | null
  firstname?: string | null
  lastname?: string | null
  company?: string | null
  street?: string | null
  street_number?: string | null
  address_line_2?: string | null
  zip?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
}

// Entspricht 1:1 der formatOrder()-Ausgabe des Backends
export interface Order {
  id: string
  db_id: number
  number: string
  portal_account_id: number | null
  platform: string | null
  date: string
  external_order_id: string | null
  currency_id: string | null
  payment_method_id: string | null
  invoice_number: string | null
  invoice_amount: string | null
  paid_amount: string | null
  infos: string | null
  pack_infos: string | null
  hidden_infos: string | null
  customer: {
    email: string | null
    username: string | null
    phone_1: string | null
    phone_2: string | null
    fax: string | null
    vat_id: string | null
    comment: string | null
  }
  shipping: {
    fee: string | null
    date: string | null
    deliverer: string | null
    code: string | null
    links: string[]
    method: string | null
    platform_status: string | null
    amazon_fba: boolean
  }
  status: {
    is_initial_contact: boolean
    is_initial_contact_at: string | null
    is_checkout: boolean
    is_checkout_at: string | null
    is_paid: boolean
    is_paid_at: string | null
    is_packed: boolean
    is_packed_at: string | null
    is_shipped: boolean
    is_shipped_at: string | null
    is_arrived: boolean
    is_arrived_at: string | null
    is_cancelled: boolean
    is_cancelled_at: string | null
    is_parked: boolean
    is_parked_at: string | null
  }
  credit: {
    main_id: number | null
    sub_ids: number[]
  }
  return_label_id: number | null
  lines: OrderLine[]
  /** Summe je Sternnummer ueber alle Positionen; bewusst ohne Gesamtzahl (Einheit nicht gesichert). */
  stueckliste_summe?: {
    teile: Array<{ kopf_id: number; merchant_sku: string; name: string | null; menge: number }>
    vollstaendig: boolean
  }
  auftrags_art?: { storniert: boolean; fba: boolean; gutschrift: boolean }
  addresses: {
    billing: OrderAddress | null
    shipping: OrderAddress | null
  }
  created_at: string
  updated_at: string
}

export interface OrderListParams {
  view?: string
  search?: string
  page?: number
  limit?: number
}

export const ORDER_SEARCH_MIN_LENGTH = 3

/** Etwas eingetippt, aber (ohne fuehrendes #) unter 3 Zeichen: das Backend lehnt ab, die Liste fragt gar nicht erst an. */
export function orderSearchTooShort(search: string | undefined): boolean {
  const s = (search ?? '').trim().replace(/^#/, '').trim()
  return s.length > 0 && s.length < ORDER_SEARCH_MIN_LENGTH
}

export interface OrderListResult {
  orders: Order[]
  has_more: boolean
}

// Reiter der Auftragsliste: `id` ist die Backend-Ansicht (`GET /order/?view=`).
// Prod 22.10: payment_method_id ist leer, Gutschriften/Retouren/Mahnungen gibt es nicht,
// is_arrived fast nie gesetzt — diese Reiter haetten immer 0 oder alles gezeigt und fehlen.
export const ORDER_VIEWS: Array<{ id: string; label: string }> = [
  { id: 'unshipped', label: 'Unerledigt' },
  { id: 'today', label: 'Heute' },
  { id: 'yesterday', label: 'Gestern' },
  { id: 'this_week', label: 'Diese Woche' },
  { id: 'last_month', label: 'Letzter Monat' },
  { id: 'shipped', label: 'Erledigt' },
  { id: 'paid', label: 'Bezahlt' },
  { id: 'amazon', label: 'Amazon' },
  { id: 'fba', label: 'Amazon FBA' },
  { id: 'ebay', label: 'eBay' },
  { id: 'parked', label: 'Geparkt' },
  { id: 'cancelled', label: 'Storniert' },
  { id: 'all', label: 'Alle' },
]

// Alte deutsche Ids aus Lesezeichen (bis 22.10 in der Adresse).
const ALTE_ANSICHTEN: Record<string, string> = {
  unerledigt: 'unshipped', heute: 'today', gestern: 'yesterday', diese_woche: 'this_week',
  letzter_monat: 'last_month', erledigt: 'shipped', bezahlt: 'paid', amazon_fba: 'fba',
  geparkt: 'parked', storniert: 'cancelled', alle: 'all',
}

/** Ansicht aus der Adresse: Backend-Id, alte deutsche Id oder sonst `all`. */
export function normalizeOrderView(view: string | null | undefined): string {
  if (!view) return 'all'
  if (ORDER_VIEWS.some((v) => v.id === view)) return view
  return ALTE_ANSICHTEN[view] ?? 'all'
}

export async function listOrders(params: OrderListParams = {}): Promise<OrderListResult> {
  const qs = new URLSearchParams()
  if (params.view && params.view !== 'all') {
    qs.set('view', params.view)
  }
  // Freitextsuche: eigener Parameter `q`, nicht die Filter-DSL (die verwirft unbekannte Felder still).
  // Unter 3 Zeichen (ohne fuehrendes #) lehnt das Backend ab: zu teuer auf der ganzen Auftragsliste.
  const search = params.search?.trim() ?? ''
  if (search.replace(/^#/, '').trim() && !orderSearchTooShort(search)) {
    qs.set('q', search)
  }
  if (params.page && params.page > 1) qs.set('pagination[page]', String(params.page))
  qs.set('pagination[limit]', String(params.limit ?? 50))

  const res = await apiFetchFull<Order[]>(`/order/?${qs.toString()}`)
  return {
    orders: res.data ?? [],
    has_more: res.pagination?.has_more ?? false,
  }
}

export async function getOrder(id: string): Promise<Order> {
  const res = await api.get<{ order: Order }>(`/order/${encodeURIComponent(id)}/`)
  return res.order
}

export async function updateOrder(id: string, fields: Record<string, unknown>): Promise<Order> {
  const res = await api.post<{ order: Order }>(`/order/${encodeURIComponent(id)}/`, fields)
  return res.order
}

export interface HistoryEntry {
  id: number
  type: string // 'status' | 'comment' | 'system' | 'shipping'
  message: string
  actor_name: string | null
  is_internal: boolean
  created_at: string
}

export async function getOrderHistory(id: string): Promise<HistoryEntry[]> {
  const res = await api.get<{ history: HistoryEntry[] }>(`/order/${encodeURIComponent(id)}/history/`)
  return res.history
}

/** Lagerbuchung eines Auftrags (Lager-History wie in DreamRobot). */
export interface OrderStockHistoryEntry {
  id: number
  created_at: string
  user: string
  stock_sku: string | null
  merchant_sku: string | null
  name: string | null
  channel: string | null
  type: string
  quantity_before: number
  quantity: number
  quantity_after: number
  note: string | null
}

export async function getOrderStockHistory(id: string): Promise<OrderStockHistoryEntry[]> {
  const res = await api.get<{ stock_history: OrderStockHistoryEntry[] }>(`/order/${encodeURIComponent(id)}/stock-history/`)
  return res.stock_history
}

export async function addOrderComment(id: string, message: string): Promise<HistoryEntry | null> {
  const res = await api.post<{ history: HistoryEntry | null }>(`/order/${encodeURIComponent(id)}/history/`, { message })
  return res.history
}
